import { useEffect, useState } from 'react';
import { requester } from 'App';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styles from './StoreStockManagement.module.css';
import NiceModal from '@ebay/nice-modal-react';

function StoreStockManagement() {
    const { storeId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [items, setItems] = useState([]);
    const [storeInfo, setStoreInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [editingItemId, setEditingItemId] = useState(null);
    const [newQuantity, setNewQuantity] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    // 페이징 관련 상태
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [lowStockCurrentPage, setLowStockCurrentPage] = useState(1);
    const [lowStockItemsPerPage, setLowStockItemsPerPage] = useState(5);

    useEffect(() => {
        if (location.state) {
            setStoreInfo(location.state.data);
        }
    }, [location])

    // 페이지 로드 시 상품 데이터 가져오기
    useEffect(() => {
        setIsLoading(true);
    }, [storeId]);

    useEffect(() => {
        if (storeInfo) {
            getStock();
        }
    }, [storeInfo])

    // 상품 재고 정보 가져오기
    const getStock = async () => {
        let data = {
            storeCode: storeInfo.storeCode
        };
        await requester.findAllVendorProducts(data, (result) => {
            console.log(result.data);
            setItems(result.data);
            setIsLoading(false);
        });
    };

    // 재고 수량 업데이트
    const updateQuantity = (itemId, quantity) => {
        setIsLoading(true);
        let data = { id: itemId, quantity: parseInt(quantity) };

        requester.updateStoreProductQuantity(data, (result) => {
            if (result.data) {
                getStock();
                setEditingItemId(null);
                setNewQuantity("");
            } else {
                setIsLoading(false);
            }
        });
    };

    // 수정 폼 제출 처리
    const handleSubmit = (itemId) => {
        if (newQuantity && !isNaN(newQuantity) && parseInt(newQuantity) >= 0) {
            updateQuantity(itemId, newQuantity);
        }
    };

    // 수정 취소
    const cancelEdit = () => {
        setEditingItemId(null);
        setNewQuantity("");
    };

    // 검색 처리
    const filteredItems = items.filter(item =>
        item.productName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // 정렬 처리
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const sortedItems = [...filteredItems].sort((a, b) => {
        if (!sortConfig.key) return 0;

        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue < bValue) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
    });

    // 정렬 방향 표시 아이콘
    const getSortDirectionIcon = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'ascending' ? '↑' : '↓';
    };

    // 페이징 처리
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedItems.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedItems.length / itemsPerPage);

    // 페이지 변경 핸들러
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // 페이지당 아이템 수 변경 핸들러
    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1); // 페이지당 아이템 수 변경 시 첫 페이지로 이동
    };

    // 재고 부족 상품 페이징 처리
    const lowStockItems = items.filter(item => item.quantity < 20)
        .sort((a, b) => a.quantity - b.quantity);
    const indexOfLastLowStockItem = lowStockCurrentPage * lowStockItemsPerPage;
    const indexOfFirstLowStockItem = indexOfLastLowStockItem - lowStockItemsPerPage;
    const currentLowStockItems = lowStockItems.slice(indexOfFirstLowStockItem, indexOfLastLowStockItem);
    const totalLowStockPages = Math.ceil(lowStockItems.length / lowStockItemsPerPage);

    // 재고 부족 상품 페이지 변경 핸들러
    const handleLowStockPageChange = (pageNumber) => {
        setLowStockCurrentPage(pageNumber);
    };

    const handleProductAdded = (newProduct) => {
        // 새로 추가된 상품 반영 및 재고 리스트 갱신
        getStock();
    };

    // 페이지네이션 컴포넌트
    const Pagination = ({ currentPage, totalPages, onPageChange, containerClass }) => {
        const pageNumbers = [];

        // 표시할 페이지 번호 범위 계산
        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(totalPages, startPage + 4);

        if (endPage - startPage < 4 && totalPages > 5) {
            startPage = Math.max(1, endPage - 4);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <div className={`${styles.pagination} ${containerClass || ''}`}>
                <button
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1}
                    className={styles.pageButton}
                >
                    &laquo;
                </button>
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={styles.pageButton}
                >
                    &lt;
                </button>

                {pageNumbers.map(number => (
                    <button
                        key={number}
                        onClick={() => onPageChange(number)}
                        className={`${styles.pageButton} ${currentPage === number ? styles.activePage : ''}`}
                    >
                        {number}
                    </button>
                ))}

                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={styles.pageButton}
                >
                    &gt;
                </button>
                <button
                    onClick={() => onPageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className={styles.pageButton}
                >
                    &raquo;
                </button>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.flexGrid}>
                <div className={`${styles.flexBox} ${styles.fullWidth}`}>
                    <div className={styles.card} style={{ width: "max-content", padding: "5px" }}>
                        <button
                            className={styles.backButton}
                            onClick={() => navigate(`/admin/store/management`)}
                            style={{ fontSize: "16px", fontWeight: "700" }}
                        >
                            &lt; 뒤로가기
                        </button>
                    </div>
                </div>
                {/* 재고 요약 정보 카드 */}
                <div className={styles.flexBox}>
                    <div className={styles.card}>
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>재고 요약</div>
                            <div className={styles.sectionContent}>
                                <div className={styles.summaryStats}>
                                    <div className={styles.statItem}>
                                        <div className={styles.statLabel}>총 상품 수</div>
                                        <div className={styles.statValue}>{items.length}</div>
                                    </div>
                                    <div className={styles.statItem}>
                                        <div className={styles.statLabel}>재고 부족 상품</div>
                                        <div className={`${styles.statValue} ${styles.warningText}`}>
                                            {items.filter(item => item.quantity < 20).length}
                                        </div>
                                    </div>
                                    <div className={styles.statItem}>
                                        <div className={styles.statLabel}>총 재고량</div>
                                        <div className={styles.statValue}>
                                            {items.reduce((sum, item) => sum + item.quantity, 0)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 재고 부족 상품 리스트 */}
                <div className={`${styles.flexBox} ${styles.wideBox}`}>
                    <div className={styles.card}>
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>재고 부족 상품</div>
                            <div className={styles.sectionContent}>
                                {isLoading ? (
                                    <div className={styles.loading}>데이터를 불러오는 중...</div>
                                ) : lowStockItems.length > 0 ? (
                                    <>
                                        <div className={styles.lowStockList}>
                                            {currentLowStockItems.map(item => (
                                                <div key={item.id} className={styles.lowStockItem}>
                                                    <div className={styles.lowStockItemContent}>
                                                        {item.liquidImg ? (
                                                            <img
                                                                src={item.liquidImg}
                                                                alt={item.productName}
                                                                className={styles.lowStockProductImage}
                                                            />
                                                        ) : (
                                                            <div className={styles.lowStockNoImage}>이미지 없음</div>
                                                        )}
                                                        <div className={styles.productInfo}>
                                                            <div className={styles.productName}>{item.productName}</div>
                                                            <div className={styles.productQuantity}>
                                                                재고: <span className={styles.warningText}>{item.quantity}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            setEditingItemId(item.id);
                                                            setNewQuantity(item.quantity.toString());
                                                        }}
                                                        className={styles.quickEditButton}
                                                    >
                                                        빠른 수정
                                                    </button>
                                                </div>
                                            ))}
                                        </div>

                                        {totalLowStockPages > 1 && (
                                            <Pagination
                                                currentPage={lowStockCurrentPage}
                                                totalPages={totalLowStockPages}
                                                onPageChange={handleLowStockPageChange}
                                                containerClass={styles.lowStockPagination}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <div className={styles.noData}>
                                        재고 부족 상품이 없습니다.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className={`${styles.flexBox} ${styles.fullWidth}`}>
                    <div className={styles.card}>
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>
                                <div className={styles.titleWithControls}>
                                    <div className={styles.titleSection}>

                                        <h2>{storeInfo ? storeInfo.storeName : '매장'} 재고 관리</h2>
                                        <button
                                            onClick={() => NiceModal.show("addProductToStoreModal", {
                                                storeCode: storeInfo.storeCode,
                                                storeName: storeInfo.storeName,
                                                onProductAdded: handleProductAdded
                                            })}
                                            className={styles.confirmButton}
                                        >
                                            새 물품 추가
                                        </button>
                                    </div>
                                    <div className={styles.searchSection}>
                                        <input
                                            type="text"
                                            placeholder="상품명 검색..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className={styles.searchInput}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={styles.sectionContent}>
                                {isLoading ? (
                                    <div className={styles.loading}>데이터를 불러오는 중...</div>
                                ) : sortedItems.length > 0 ? (
                                    <>
                                        <div className={styles.tableControls}>
                                            <div className={styles.itemsInfo}>
                                                전체 {sortedItems.length}개 항목 중 {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, sortedItems.length)}
                                            </div>
                                            <div className={styles.itemsPerPageControl}>
                                                <label htmlFor="itemsPerPage">페이지당 항목:</label>
                                                <select
                                                    id="itemsPerPage"
                                                    value={itemsPerPage}
                                                    onChange={handleItemsPerPageChange}
                                                    className={styles.itemsPerPageSelect}
                                                >
                                                    <option value={5}>5</option>
                                                    <option value={10}>10</option>
                                                    <option value={20}>20</option>
                                                    <option value={50}>50</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className={styles.tableContainer}>
                                            <table className={styles.stockTable}>
                                                <thead>
                                                    <tr>
                                                        <th>이미지</th>
                                                        <th onClick={() => requestSort('productName')}>
                                                            상품명 {getSortDirectionIcon('productName')}
                                                        </th>
                                                        <th onClick={() => requestSort('quantity')}>
                                                            재고 수량 {getSortDirectionIcon('quantity')}
                                                        </th>
                                                        <th>재고 상태</th>
                                                        <th>관리</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {currentItems.map((item) => (
                                                        <tr key={item.id} className={item.quantity < 20 ? styles.lowStock : ''}>
                                                            <td className={styles.imageCell}>
                                                                {item.liquidImg ? (
                                                                    <img
                                                                        src={item.liquidImg}
                                                                        alt={item.productName}
                                                                        className={styles.productImage}
                                                                    />
                                                                ) : (
                                                                    <div className={styles.noImage}>이미지 없음</div>
                                                                )}
                                                            </td>
                                                            <td>{item.productName}</td>
                                                            <td className={item.quantity < 20 ? styles.warningText : ''}>
                                                                {item.quantity}
                                                            </td>
                                                            <td>
                                                                {item.quantity < 20 ? (
                                                                    <span className={styles.warningBadge}>재고 부족</span>
                                                                ) : (
                                                                    <span className={styles.normalBadge}>정상</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {editingItemId === item.id ? (
                                                                    <div className={styles.editControls}>
                                                                        <input
                                                                            type="number"
                                                                            value={newQuantity}
                                                                            onChange={(e) => setNewQuantity(e.target.value)}
                                                                            className={styles.quantityInput}
                                                                            min="0"
                                                                            autoFocus
                                                                        />
                                                                        <button
                                                                            onClick={() => handleSubmit(item.id)}
                                                                            className={styles.confirmButton}
                                                                        >
                                                                            확인
                                                                        </button>
                                                                        <button
                                                                            onClick={cancelEdit}
                                                                            className={styles.cancelButton}
                                                                        >
                                                                            취소
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => {
                                                                            setEditingItemId(item.id);
                                                                            setNewQuantity(item.quantity.toString());
                                                                        }}
                                                                        className={styles.editButton}
                                                                    >
                                                                        수정
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {totalPages > 1 && (
                                            <Pagination
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                                onPageChange={handlePageChange}
                                                containerClass={styles.mainPagination}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <div className={styles.noData}>
                                        {searchTerm ? '검색 결과가 없습니다.' : '등록된 상품이 없습니다.'}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default StoreStockManagement; 